import { create } from "zustand";
import { persist } from "zustand/middleware";
import { disconnect as disconnectYoutube } from "../social/youtubeAccount.js";
import { FREE_LIMITS, requireFeature } from "../library/proFeatures.js";

/**
 * Viewer profiles (Section 13). Up to five, entirely on-device.
 *
 * MIGRATION, AND WHY THERE ISN'T ONE: favorites and watch progress have always
 * been keyed `${profileId}:...` with profileId hardcoded to "local". Rather
 * than rewrite every existing row to a freshly generated id — a destructive
 * migration whose failure mode is a user silently losing their favorites — the
 * first profile simply *takes* that id. Existing data therefore already belongs
 * to profile #1 the moment profiles arrive, and nothing has to move.
 */
export const DEFAULT_PROFILE_ID = "local";

/** Netflix caps at 5; the roadmap asks for the 6th to be refused clearly. */
export const MAX_PROFILES = 5;

/** Avatars are emoji: no image assets to ship, scale or localise, and they
 * render identically on a TV, a phone and a desktop. */
export const PROFILE_AVATARS = ["😀", "😎", "🦊", "🐼", "🚀", "🎬", "⚽", "🎧", "🌙", "🍿"] as const;

export interface Profile {
  id: string;
  name: string;
  avatar: string;
  /**
   * A Kids profile forces the parental category filter on regardless of the
   * parental store's own setting, and leaving it requires the PIN when one is
   * set (see canLeaveProfileWithoutPin).
   */
  isKids: boolean;
  createdAt: string;
}

export interface ProfileStore {
  profiles: Profile[];
  activeProfileId: string;
  /** Create the default profile on first run so nothing is ever unowned. */
  ensureDefault: () => void;
  /** Returns an error message when the profile could not be added. */
  addProfile: (input: { name: string; avatar: string; isKids: boolean }) => string | null;
  updateProfile: (id: string, patch: Partial<Omit<Profile, "id" | "createdAt">>) => void;
  /** Returns an error message when the profile could not be removed. */
  removeProfile: (id: string) => string | null;
  setActiveProfile: (id: string) => void;
}

function makeId(): string {
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

const DEFAULT_PROFILE: Profile = {
  id: DEFAULT_PROFILE_ID,
  name: "Me",
  avatar: PROFILE_AVATARS[0],
  isKids: false,
  createdAt: new Date(0).toISOString(),
};

export const useProfiles = create<ProfileStore>()(
  persist(
    (set, get) => ({
      profiles: [DEFAULT_PROFILE],
      activeProfileId: DEFAULT_PROFILE_ID,

      ensureDefault: () =>
        set((state) => {
          if (state.profiles.length > 0) return state;
          return { profiles: [DEFAULT_PROFILE], activeProfileId: DEFAULT_PROFILE_ID };
        }),

      addProfile: ({ name, avatar, isKids }) => {
        const { profiles } = get();
        // A free viewer has one profile (ADR 2026-10-10). Existing ones are never removed.
        if (profiles.length >= FREE_LIMITS.profiles && !requireFeature("profiles")) {
          return "More than one profile is part of Pro.";
        }
        if (profiles.length >= MAX_PROFILES) {
          return `You can have up to ${MAX_PROFILES} profiles. Remove one to add another.`;
        }
        const trimmed = name.trim();
        if (!trimmed) return "Give the profile a name.";
        if (profiles.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
          return "There is already a profile with that name.";
        }
        set({
          profiles: [
            ...profiles,
            { id: makeId(), name: trimmed, avatar, isKids, createdAt: new Date().toISOString() },
          ],
        });
        return null;
      },

      updateProfile: (id, patch) =>
        set((state) => ({
          profiles: state.profiles.map((p) => (p.id === id ? { ...p, ...patch } : p)),
        })),

      removeProfile: (id) => {
        const { profiles, activeProfileId } = get();
        if (profiles.length <= 1) {
          // Deleting the last profile would leave every favorite and resume
          // point owned by a profile that no longer exists.
          return "You need at least one profile.";
        }
        const remaining = profiles.filter((p) => p.id !== id);
        // A removed profile's YouTube sign-in is revoked at Google and deleted from this TV
        // (fire and forget: removing the profile never waits on the network).
        void disconnectYoutube(id);
        set({
          profiles: remaining,
          activeProfileId:
            activeProfileId === id ? (remaining[0]?.id ?? DEFAULT_PROFILE_ID) : activeProfileId,
        });
        return null;
      },

      setActiveProfile: (id) =>
        set((state) => {
          if (!state.profiles.some((p) => p.id === id)) return state;
          if (state.activeProfileId === id) return state;
          // Profile-owned stores subscribe to this change and synchronously
          // replace their active view before the newly selected profile renders.
          return { activeProfileId: id };
        }),
    }),
    {
      name: "slique-profiles",
      partialize: (state) => ({ profiles: state.profiles, activeProfileId: state.activeProfileId }),
      // Step 4 of the section: reopening returns to the last-used profile
      // rather than the picker, so activeProfileId is persisted deliberately.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ProfileStore>;
        const profiles = Array.isArray(p.profiles) && p.profiles.length > 0 ? p.profiles : [DEFAULT_PROFILE];
        const activeExists = profiles.some((x) => x.id === p.activeProfileId);
        return {
          ...current,
          profiles,
          activeProfileId: activeExists ? (p.activeProfileId as string) : (profiles[0]?.id ?? DEFAULT_PROFILE_ID),
        };
      },
    },
  ),
);

/**
 * Active profile id for non-React callers (the favorites and progress stores
 * write from plain functions). Reads the live store rather than caching, so a
 * profile switch takes effect on the very next write.
 */
export function activeProfileId(): string {
  return useProfiles.getState().activeProfileId;
}

/** The active profile record, or the default when something is inconsistent. */
export function activeProfile(): Profile {
  const { profiles, activeProfileId: id } = useProfiles.getState();
  return profiles.find((p) => p.id === id) ?? profiles[0] ?? DEFAULT_PROFILE;
}

/**
 * Whether leaving `from` needs a PIN. Only a Kids profile is gated, and only
 * when a PIN actually exists — otherwise a parent who never set one would be
 * locked out of their own app with no way back.
 */
export function profileSwitchNeedsPin(from: Profile | undefined, pinIsSet: boolean): boolean {
  return Boolean(from?.isKids) && pinIsSet;
}
